'use client'

import { useState, useEffect, ChangeEvent } from 'react'
import { ImageIcon } from 'lucide-react'
import { useEditorContext } from '@/components/ui/editor'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

const toBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('FileReader result is not a string'))
      }
    }
    reader.onerror = error => reject(error)
  })
}

const Picture = () => {
  const { editor, sourceMode } = useEditorContext()

  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('url') // 'url' | 'file'

  const [imageUrl, setImageUrl] = useState('')
  const [altTextUrl, setAltTextUrl] = useState('')

  const [fileBase64, setFileBase64] = useState('')
  const [altTextFile, setAltTextFile] = useState('')

  useEffect(() => {
    if (!open) {
      // Reset all when popover closes
      setImageUrl('')
      setAltTextUrl('')
      setFileBase64('')
      setAltTextFile('')
      setTab('url')
    }
  }, [open])

  const insertImage = (src: string, alt: string) => {
    if (!src) {
      return
    }
    editor.chain().focus().setImage({ src, alt }).run()
    setOpen(false)
  }

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return
    }
    try {
      const base64 = await toBase64(e.target.files[0])
      setFileBase64(base64)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Tooltip>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant={editor.isActive('image') ? 'default' : 'ghost'}
              title="Add Image"
              disabled={sourceMode}
              tabIndex={-1}>
              <ImageIcon />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>
          <p>Add picture</p>
        </TooltipContent>

        <PopoverContent className="w-80">
          <Tabs className="w-full" value={tab} onValueChange={setTab}>
            <TabsList className="w-full">
              <TabsTrigger value="url">URL</TabsTrigger>
              <TabsTrigger value="file">File</TabsTrigger>
            </TabsList>

            <TabsContent value="url" className="mt-4 space-y-4">
              <Input type="url" placeholder="Image URL" value={imageUrl} onChange={e => setImageUrl(e.target.value)} />
              <Input
                type="text"
                placeholder="Alt text (optional)"
                value={altTextUrl}
                onChange={e => setAltTextUrl(e.target.value)}
              />
              <Button
                type="button"
                className="w-full"
                disabled={!imageUrl.trim()}
                onClick={() => insertImage(imageUrl.trim(), altTextUrl.trim())}>
                Insert Image
              </Button>
            </TabsContent>

            <TabsContent value="file" className="mt-4 space-y-4">
              <Input type="file" accept="image/*" onChange={onFileChange} />
              <Input
                type="text"
                placeholder="Alt text (optional)"
                value={altTextFile}
                onChange={e => setAltTextFile(e.target.value)}
                disabled={!fileBase64}
              />
              <Button
                type="button"
                className="w-full"
                disabled={!fileBase64}
                onClick={() => insertImage(fileBase64, altTextFile.trim())}>
                Insert Image
              </Button>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>
    </Tooltip>
  )
}

export default Picture
